/* eslint-disable react/prop-types */
import * as React from 'react';

import { useLockFn, useUpdateEffect } from 'ahooks';
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Image,
  Modal,
  notification,
  Select,
  Space,
  Typography,
} from 'antd';

import {
  AntdRouterExternalLink,
  AntdRouterLink,
  DeleteOutlinedIcon,
  EditOutlinedIcon,
  MoreOutlinedIcon,
  PrimitiveTable,
} from 'components';
import { deleteProductComment } from 'services';
import type { ProductCommentStatus, ProductCommentSummary } from 'types';
import {
  adjustPageNumber,
  dateTimeFormat,
  FILE,
  generateDownloadFile,
  generateTableDataSource,
  localizeProductCommentStatus,
  normalizeToString,
  parseFromContent,
  PATHNAME,
} from 'utils';

const {
  DEFAULT: { SLUG_SEPARATOR: DEFAULT_SLUG_SEPARATOR },
  LOCAL: {
    PRODUCTS: { BASE_URL: PRODUCTS_BASE_URL, UPDATE_URL: PRODUCTS_UPDATE_URL },
    USERS: { BASE_URL: USERS_BASE_URL, UPDATE_URL: USERS_UPDATE_URL },
    PRODUCTS_COMMENTS: { UPDATE_URL: PRODUCTS_COMMENTS_UPDATE_URL },
  },
} = PATHNAME;
const {
  IMAGE: {
    SIZE: { WIDTH: IMAGE_SIZE_WIDTH, HEIGHT: IMAGE_SIZE_HEIGHT },
  },
} = FILE;

const productCommentStatuses = [
  'OnReview',
  'Rejected',
  'Accepted',
] satisfies ProductCommentStatus[];

type ProductsCommentsTableProps = {
  loading: boolean;
  total: number;
  productsComments: ProductCommentSummary[];
  pageSize: number;
  pageNumber: number;
  handleStatuses: (statuses: ProductCommentStatus[]) => void;
  handlePageSize: (pageSize: number) => void;
  handlePageNumber: (pageNumber: number) => void;
  handleRefreshData: () => void;
};

const ProductsCommentsTable = React.memo<ProductsCommentsTableProps>(
  ({
    loading,
    total,
    productsComments,
    pageSize,
    pageNumber,
    handleStatuses,
    handlePageSize,
    handlePageNumber,
    handleRefreshData,
  }) => {
    const [deleteModalInstance, deleteModalContextHolder] = Modal.useModal();

    useUpdateEffect(() => {
      adjustPageNumber(total, pageSize, pageNumber, handlePageNumber);
    }, [total, pageSize, pageNumber]);

    const handleDeleteProductComment = useLockFn(
      async ({ id }: ProductCommentSummary) => {
        deleteProductComment({ id })
          .then(() => {
            handleRefreshData();
            notification.success({
              message: 'دیدگاه مورد نظر با موفقیت حذف شد.',
            });
          })
          .catch(message => {
            notification.error({
              message,
            });
          });
      },
    );

    const productsCommentsDataSource = generateTableDataSource(
      productsComments,
      'id',
    );

    return (
      <PrimitiveTable
        scroll={{ x: 1400, y: 630 }}
        columns={[
          {
            title: 'محصول',
            dataIndex: 'product',
            key: 'product',
            width: '25%',
            ellipsis: true,
            render: (_, { product: { id, name, bannerId } }) => (
              <Space>
                <Image
                  src={generateDownloadFile(
                    'Product',
                    bannerId,
                    IMAGE_SIZE_WIDTH,
                    IMAGE_SIZE_HEIGHT,
                  )}
                  width={40}
                  height={40}
                  alt={name}
                />
                <AntdRouterExternalLink
                  href={`${PRODUCTS_BASE_URL}${PRODUCTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                  className='text-wrap'
                >
                  {name}
                </AntdRouterExternalLink>
              </Space>
            ),
          },
          {
            title: 'دیدگاه',
            dataIndex: 'content',
            key: 'content',
            render: (_, { content }) => {
              const parsedContent = parseFromContent(content);

              if (!parsedContent) return;

              return (
                <Typography
                  dangerouslySetInnerHTML={{
                    __html: parsedContent.innerHTML,
                  }}
                  className='text-wrap'
                />
              );
            },
          },
          {
            title: 'امتیاز',
            dataIndex: 'rate',
            key: 'rate',
            width: 70,
            ellipsis: true,
            render: (_, { rate }) => <Flex justify='center'>{rate}</Flex>,
          },
          {
            title: 'تعداد پاسخ',
            dataIndex: 'replyCount',
            key: 'replyCount',
            width: 100,
            ellipsis: true,
            render: (_, { replyCount }) => (
              <Flex justify='center'>{replyCount}</Flex>
            ),
          },
          {
            title: 'کاربر',
            dataIndex: 'user',
            key: 'user',
            width: 120,
            ellipsis: true,
            render: (_, { user }) => {
              const {
                id,
                userName,
                displayName,
                firstName,
                lastName = '',
                phoneNumber,
              } = user;

              return (
                <AntdRouterExternalLink
                  href={`${USERS_BASE_URL}${USERS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                >
                  {userName ||
                    displayName ||
                    (firstName
                      ? firstName.concat(` ${lastName}`)
                      : phoneNumber)}
                </AntdRouterExternalLink>
              );
            },
          },
          {
            title: 'وضعیت',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            ellipsis: true,
            filterDropdown: () => (
              <div className='bg-[--ant-table-header-bg] p-2'>
                <Select<ProductCommentStatus[]>
                  showSearch
                  mode='multiple'
                  maxTagCount='responsive'
                  className='w-72'
                  placeholder='وضعیت مورد نظر را انتخاب کنید'
                  options={productCommentStatuses.map(type => {
                    const { label } = localizeProductCommentStatus(type);

                    return {
                      label,
                      value: type,
                    };
                  })}
                  filterOption={(inputValue, option) =>
                    normalizeToString(option?.label)
                      .toLowerCase()
                      .includes(inputValue.toLowerCase())
                  }
                  onChange={value => {
                    handleStatuses(value);
                  }}
                />
              </div>
            ),
            render: (_, { status }) => {
              const { label, color } = localizeProductCommentStatus(status);
              return (
                <Flex justify='center'>
                  <Badge count={label} color={color} />
                </Flex>
              );
            },
          },
          {
            title: 'تاریخ ساخت',
            dataIndex: 'createdDate',
            key: 'createdDate',
            width: 170,
            ellipsis: true,
            render: (_, { createdDate }) => (
              <bdi>{dateTimeFormat(new Date(createdDate))}</bdi>
            ),
          },
          {
            title: 'تاریخ انتشار',
            dataIndex: 'publishTime',
            key: 'publishTime',
            width: 170,
            ellipsis: true,
            render: (_, { publishTime }) => {
              if (!publishTime) return null;

              return <bdi>{dateTimeFormat(new Date(publishTime))}</bdi>;
            },
          },
          {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 75,
            fixed: 'right',
            render: (_, record) => {
              const { id } = record;
              return (
                <Flex align='center' justify='center'>
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: 'update-product-comment',
                          label: (
                            <AntdRouterLink
                              to={`${PRODUCTS_COMMENTS_UPDATE_URL}${DEFAULT_SLUG_SEPARATOR}${id}${DEFAULT_SLUG_SEPARATOR}`}
                              className='inline-block py-1 text-inherit'
                            >
                              <EditOutlinedIcon />
                              <span className='ms-2 text-sm'>
                                ویرایش دیدگاه
                              </span>
                            </AntdRouterLink>
                          ),
                        },
                        {
                          key: 'delete-product-comment',
                          label: (
                            <React.Fragment>
                              <Button
                                danger
                                type='link'
                                size='small'
                                className='my-0.5 flex items-center px-0'
                                onClick={async () => {
                                  const confirmed =
                                    await deleteModalInstance.confirm({
                                      title:
                                        'آیا از حذف دیدگاه مورد نظر اطمینان دارید؟',
                                      width: 500,
                                    });
                                  if (confirmed) {
                                    handleDeleteProductComment(record);
                                  }
                                }}
                              >
                                <DeleteOutlinedIcon />
                                <span className='text-sm'>حذف دیدگاه</span>
                              </Button>
                              {deleteModalContextHolder}
                            </React.Fragment>
                          ),
                        },
                      ],
                    }}
                    trigger={['click']}
                  >
                    <Button size='small' className='pb-[18px] pt-3.5'>
                      <MoreOutlinedIcon className='text-xl' />
                      <span className='sr-only'>آیکون اکشن‌ها</span>
                    </Button>
                  </Dropdown>
                </Flex>
              );
            },
          },
        ]}
        loading={loading}
        dataSource={productsCommentsDataSource}
        pagination={{
          total,
          pageSize,
          current: pageNumber,
          showTotal: () => `در مجموع ${total} مورد`,
          onChange: (page, size) => {
            handlePageSize(size);
            if (page !== pageNumber) {
              handlePageNumber(page);
            }
          },
        }}
      />
    );
  },
);
ProductsCommentsTable.displayName = 'ProductsCommentsTable';

export { ProductsCommentsTable };
